import { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Confetti from 'react-confetti';
import { showToast } from '@/utils/toast';
import { useUserProfile, useCheckIn } from '@hooks';
import { useWindowSize } from '@/shared/hooks/useWindowSize';
import { Loading } from '@/components/common';
import { LockIcon, FireIcon } from '@/components/icons';
import type { DailyReward } from '@types';
import questDailyTask from '@/assets/quests/quest-daily-task.webp';
import questDailyCheckin from '@/assets/quests/quest-daily-checkin.webp';
import questLock from '@/assets/quests/quest-lock.webp';
import questCheckinGold from '@/assets/quests/quest-checkin-gold.webp';
import questCheckinSuccess from '@/assets/quests/quest-checkin-success.webp';

interface DailyRewardCardProps {
  reward: DailyReward;
  onClaim: (day: number) => void;
  isLastDay?: boolean;
  isToday?: boolean;
  isLoading?: boolean;
}

const DailyRewardCard = ({
  reward,
  onClaim,
  isLastDay = false,
  isToday = false,
  isLoading = false,
}: DailyRewardCardProps) => {
  const { day, rewardAmount, status } = reward;

  const handleClick = () => {
    if (status === 'CLAIMABLE') {
      onClaim(day);
    } else if (status === 'LOCKED') {
      showToast.error('This day is not available yet!', {
        style: {
          background: '#2E4056',
          color: '#fff',
        },
      });
    }
  };

  return (
    <motion.div
      className={`flex flex-col items-center p-0 isolate bg-[#C2CCDF] rounded-2xl ${
        isLastDay ? 'w-full h-[154px]' : 'w-[108px] h-[102px]'
      } ${status === 'LOCKED' ? 'cursor-pointer' : ''} ${status === 'CLAIMABLE' ? 'cursor-pointer' : ''}`}
      onClick={handleClick}
      whileHover={status === 'CLAIMABLE' ? { scale: 1.02 } : {}}
      whileTap={status === 'CLAIMABLE' ? { scale: 0.98 } : {}}
    >
      <div
        className={`flex items-center justify-center h-[30px] font-normal text-[26px] leading-[30px] text-center text-[#2E4056] ${
          isLastDay ? 'w-full' : 'w-[108px]'
        }`}
      >
        Day {day}
      </div>

      <div
        className={`relative flex flex-col items-center justify-between bg-[#ACBBD0] shadow-[inset_0px_2px_8px_rgba(0,0,0,0.25)] self-stretch flex-grow-0 rounded-b-2xl ${
          isLastDay ? 'h-[124px] py-4 px-3' : 'h-[72px] py-2 px-3'
        }`}
      >
        <div
          className={`${isLastDay ? 'w-[100px] h-[100px]' : 'w-[60px] h-[60px]'} drop-shadow-[1px_2px_4px_#2E4056]`}
        >
          <img
            src={questCheckinGold}
            alt=""
            className="w-full h-full object-contain"
          />
        </div>

        <div
          className={`absolute inset-0 font-normal text-[20px] leading-[23px] text-center self-stretch ${isLastDay ? 'mt-[20%]' : 'mt-[35%]'}`}
          style={{
            color: '#FFE500',
            WebkitTextStroke: '1px #000000',
            textShadow: '1px 2px 0px #000000',
          }}
        >
          {rewardAmount} Gold
        </div>
      </div>

      {status === 'CLAIMED' && (
        <div
          className={`absolute inset-0 flex items-center justify-center bg-black/60 rounded-2xl ${
            isLastDay ? 'w-full h-[154px]' : 'w-[108px] h-[102px]'
          }`}
        >
          <img src={questCheckinSuccess} alt="" className="w-14 h-14" />
        </div>
      )}

      {status === 'LOCKED' && (
        <div
          className={`absolute inset-0 flex items-center justify-center bg-black/50 rounded-2xl ${
            isLastDay ? 'w-full h-[154px]' : 'w-[108px] h-[102px]'
          }`}
        >
          <LockIcon size={isLastDay ? 64 : 48} />
        </div>
      )}

      {isLoading && (
        <div
          className={`absolute inset-0 flex items-center justify-center bg-black/60 rounded-2xl ${
            isLastDay ? 'w-full h-[154px]' : 'w-[108px] h-[102px]'
          }`}
        >
          <motion.div
            className="w-10 h-10 border-4 border-white/30 border-t-white rounded-full"
            animate={{ rotate: 360 }}
            transition={{
              duration: 1,
              repeat: Infinity,
              ease: 'linear',
            }}
          />
        </div>
      )}

      {isToday && (
        <div className="absolute -top-2 -right-2 bg-gradient-to-r from-yellow-400 to-yellow-500 px-2 py-1 rounded-full border-2 border-black shadow-lg z-10 hidden">
          <span className="text-xs font-bold text-black">TODAY</span>
        </div>
      )}
    </motion.div>
  );
};

const TaskIcons = () => {
  return (
    <div className="flex flex-row items-start p-0 gap-2 isolate w-full h-12">
      <motion.div
        className="flex flex-row justify-center items-center p-1 gap-1 flex-1 h-12 bg-white/15 backdrop-blur-sm rounded-t-xl"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <div className="w-10 h-10 drop-shadow-[0px_4px_4px_rgba(0,0,0,0.25)]">
          <img src={questDailyCheckin} alt="" />
        </div>
      </motion.div>

      <div className="flex-1 relative">
        <motion.div
          className="flex flex-row justify-center items-center p-1 gap-1 h-12 bg-white/10 border-2 border-[rgba(216,230,234,0.06)] border-b-0 shadow-[inset_0px_-4px_4px_rgba(0,0,0,0.25)] backdrop-blur-md rounded-t-xl"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="w-10 h-10">
            <img src={questDailyTask} alt="" />
          </div>
        </motion.div>
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-t-xl pointer-events-none">
          <img src={questLock} alt="" className="w-full h-full mt-2" />
        </div>
      </div>
    </div>
  );
};

export const QuestsContent = () => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [isClaimingDay, setIsClaimingDay] = useState<number | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);

  const { width, height } = useWindowSize();
  const { data: userProfile, isLoading, isError } = useUserProfile();

  const checkInMutation = useCheckIn();

  useEffect(() => {
    if (!showConfetti) return;

    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 6000);

    return () => clearTimeout(timer);
  }, [showConfetti]);

  const totalCheckIn = userProfile?.totalCheckIn || 0;
  const lastCheckInAt = userProfile?.lastCheckInAt;
  const streak = userProfile?.streak ?? 0;

  const dailyRewards = useMemo(() => {
    const rewardsPattern = [1, 1, 2, 2, 3, 3, 5];

    const hasCheckedInToday = lastCheckInAt
      ? (() => {
          const today = new Date();
          const todayUTC = Date.UTC(
            today.getUTCFullYear(),
            today.getUTCMonth(),
            today.getUTCDate()
          );

          const lastCheckIn = new Date(lastCheckInAt);
          const lastCheckInUTC = Date.UTC(
            lastCheckIn.getUTCFullYear(),
            lastCheckIn.getUTCMonth(),
            lastCheckIn.getUTCDate()
          );

          return todayUTC === lastCheckInUTC;
        })()
      : false;

    // Week number for day labels (Day 1-7, Day 8-14, ...)
    const currentWeek = Math.floor(totalCheckIn / 7);
    const startDay = currentWeek * 7 + 1;

    const rewards: DailyReward[] = [];
    for (let i = 0; i < 7; i++) {
      const streakPosition = i + 1;
      const day = startDay + i;
      let status: 'CLAIMED' | 'CLAIMABLE' | 'LOCKED';

      // Status based on streak
      if (streakPosition <= streak) {
        status = 'CLAIMED';
      } else if (streakPosition === streak + 1 && !hasCheckedInToday) {
        status = 'CLAIMABLE';
      } else {
        status = 'LOCKED';
      }

      const rewardAmount = rewardsPattern[i];

      rewards.push({
        day,
        rewardAmount,
        status,
      });
    }

    return rewards;
  }, [totalCheckIn, lastCheckInAt, streak]);

  const handleClaim = async (day: number) => {
    if (checkInMutation.isPending || isClaimingDay !== null) return;

    try {
      setIsClaimingDay(day);

      await checkInMutation.mutateAsync();

      setShowConfetti(true);
      showToast.success('Claimed successfully!', {
        style: {
          background: '#4CAF50',
          color: '#fff',
        },
      });
    } catch (error) {
      console.error('Check-in failed:', error);
      showToast.error('Failed to claim reward. Please try again.', {
        style: {
          background: '#f44336',
          color: '#fff',
        },
      });
    } finally {
      setIsClaimingDay(null);
    }
  };

  if (isLoading) {
    return <Loading text="Loading quests" size="lg" fullScreen />;
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-white">
          <p className="text-xl mb-4">Failed to load check-in data</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-purple-600 rounded-lg hover:bg-purple-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full relative overflow-hidden flex flex-col">
      {showConfetti && (
        <div className="fixed inset-0 z-[9998] pointer-events-none">
          <Confetti
            width={width}
            height={height}
            recycle={false}
            numberOfPieces={500}
            gravity={0.3}
            colors={['#FFE500', '#4CAF50', '#2196F3', '#FF9800', '#E91E63']}
          />
        </div>
      )}
      <div className="fixed top-[130px] left-0 right-0 max-w-[var(--app-max-width)] mx-auto flex flex-col items-center px-3 gap-3 z-40 pb-4">
        <motion.div
          className="flex flex-col items-center p-0 w-[335px] gap-2 mb-2"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1
            className="w-full font-normal text-[38px] leading-[43px] text-center text-white"
            style={{
              WebkitTextStroke: '1px #000',
              textShadow: '1px 1px 0px #000',
            }}
          >
            Quests
          </h1>

          <div
            className="flex flex-col items-center p-0 gap-1"
            style={{
              textShadow: '0.5px 1px 0px #000',
            }}
          >
            <p className="font-normal text-sm leading-4 text-white">
              Daily vibes, feel-good grind
            </p>
            <p className="font-normal text-sm leading-4 text-white">
              Knock out quests, push your progress
            </p>
          </div>
        </motion.div>
      </div>

      <motion.div
        className="fixed top-[230px] bottom-0 pb-[100px] left-0 right-0 max-w-[var(--app-max-width)] mx-auto px-3 overflow-y-auto z-30 scrollbar-thin scrollbar-thumb-purple-500 scrollbar-track-purple-900/20"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="flex flex-col items-center p-0 relative w-full bg-black/60 backdrop-blur-sm rounded-2xl pt-3 px-1.5 pb-1.5 gap-4">
          <div className="flex items-center justify-center gap-4 w-full">
            <h2 className="font-normal text-2xl leading-[27px] text-white">
              Daily Check-in
            </h2>
            <div className="relative">
              <div
                className="flex items-center gap-1 pl-3 pr-4 py-1 bg-gradient-to-r from-orange-500 to-red-500 rounded-full border-2 border-yellow-400 shadow-lg cursor-pointer hover:scale-105 active:scale-95 transition-transform"
                onClick={() => setIsTooltipOpen(!isTooltipOpen)}
              >
                <FireIcon size={20} animated />
                <span
                  className="font-bold text-lg text-white"
                  style={{
                    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.8)',
                  }}
                >
                  {streak}
                </span>
              </div>

              <AnimatePresence>
                {isTooltipOpen && (
                  <>
                    <motion.div
                      className="fixed inset-0 z-40"
                      onClick={() => setIsTooltipOpen(false)}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    />

                    <motion.div
                      className="absolute right-0 top-full mt-2 z-50"
                      initial={{ opacity: 0, scale: 0.95, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -10 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="relative bg-gray-900 text-white text-sm rounded-lg px-4 py-3 shadow-xl border border-gray-700 whitespace-nowrap">
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-px">
                          <div className="border-8 border-transparent border-b-gray-900"></div>
                        </div>

                        <button
                          onClick={() => setIsTooltipOpen(false)}
                          className="absolute top-2 right-2 w-5 h-5 flex items-center justify-center rounded-full bg-gray-700 hover:bg-gray-600 transition-colors"
                        >
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M10 2L2 10M2 2L10 10"
                              stroke="white"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                        </button>

                        <div className="font-bold text-yellow-400 mb-2 flex items-center gap-2">
                          <FireIcon size={16} animated />
                          Streak System
                        </div>
                        <div className="space-y-1 text-xs">
                          <p>
                            <span className="text-gray-400">Cycle:</span> 7 days
                          </p>
                          <p>
                            <span className="text-gray-400">Rewards:</span>{' '}
                            D1:1, D2:1, D3:2, D4:2, D5:3, D6:3, D7:5 Gold
                          </p>
                          <p>
                            <span className="text-gray-400">Check-ins:</span>{' '}
                            {totalCheckIn} days
                          </p>
                          <p className="text-red-400 font-semibold mt-2">
                            ⚠️ Missing 1 day = Reset streak
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div className="flex flex-col items-start p-0 w-full gap-0">
            <TaskIcons />

            <div className="flex flex-col items-start p-0 w-full bg-white/15 backdrop-blur-sm rounded-b-xl py-5 px-3 gap-4">
              <div className="flex flex-col items-start p-0 w-full gap-2 rounded-xl">
                <div className="flex flex-row items-start p-0 w-full gap-2">
                  {dailyRewards.slice(0, 3).map((reward) => (
                    <div key={reward.day} className="flex-1 relative">
                      <DailyRewardCard
                        reward={reward}
                        onClaim={handleClaim}
                        isToday={reward.status === 'CLAIMABLE'}
                        isLoading={isClaimingDay === reward.day}
                      />
                    </div>
                  ))}
                </div>

                <div className="flex flex-row items-start p-0 w-full gap-2">
                  {dailyRewards.slice(3, 6).map((reward) => (
                    <div key={reward.day} className="flex-1 relative">
                      <DailyRewardCard
                        reward={reward}
                        onClaim={handleClaim}
                        isToday={reward.status === 'CLAIMABLE'}
                        isLoading={isClaimingDay === reward.day}
                      />
                    </div>
                  ))}
                </div>

                <div className="w-full relative">
                  <DailyRewardCard
                    reward={dailyRewards[6]}
                    onClaim={handleClaim}
                    isLastDay={true}
                    isToday={dailyRewards[6].status === 'CLAIMABLE'}
                    isLoading={isClaimingDay === dailyRewards[6].day}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
